import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("bhawar_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(!user && !!getToken());

  const refresh = useCallback(async (isBackground = false) => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      localStorage.removeItem("bhawar_user");
      return;
    }

    if (!isBackground && !user) setLoading(true);

    try {
      // silent401: 401 here just means "not logged in" — do NOT bounce to /login
      const res = await api.get("/auth/me", { silent401: true, validateStatus: (s) => s < 500 });
      if (res.status === 200 && res.data) {
        setUser(res.data);
        localStorage.setItem("bhawar_user", JSON.stringify(res.data));
      } else if (res.status === 401) {
        setUser(null);
        setToken(null);
        localStorage.removeItem("bhawar_user");
      }
    } catch (_) {
      // Keep existing cached user if network temporarily hiccuped on tab resume
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();

    // Auto-refresh user session when returning to the tab or browser waking up
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh(true);
      }
    };

    window.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, [refresh]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setToken(null);
    setUser(null);
    localStorage.removeItem("bhawar_user");
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
